/**
 * Shared type definitions for the TournaHub player application.
 * These types mirror the Firestore document shapes used throughout the app.
 */

// ─── Platform Constants ─────────────────────────────────────────────────────

export const PLATFORM_FEE_PERCENT = 0.15;

// ─── Tournament Types ───────────────────────────────────────────────────────

export interface Tournament {
    id: string;
    title: string;
    game: string;
    mode: string;
    status: string;
    entryFee: number;
    maxSlots: number;
    slotFill: number;
    startTime: string;
    prizePool?: number;
    description?: string;
    rules?: string;
    roomId?: string;
    roomPass?: string;
    registrationCloseTime?: string;
    scrambled?: boolean;
    bracketData?: MobaBracketData;
    groupCount?: number;
    scrambleTime?: string;
    mvpBonus?: number;
    perKillReward?: number;
    killBudget?: number;
    [key: string]: unknown;
}

// ─── MOBA / Bracket Types ───────────────────────────────────────────────────

export interface MobaTeam {
    id: string;
    name: string;
    tag: string;
    color: string;
    region: string;
    rank: string;
    captain?: string;
    members?: (string | { ign: string; role: 'Top' | 'Jungle' | 'Mid' | 'ADC' | 'Support' })[];
    substitute?: { ign: string; role: string };
}

export interface MobaGroupMatch {
    id: string;
    groupLabel: string;
    roundLabel: string;
    team1Id: string;
    team2Id: string;
    winnerId: string | null;
    scheduledDate: string;
    scheduledTime: string;
    matchDay: number;
    mvpPlayerName?: string;
}

export interface MobaKnockoutMatch {
    id: string;
    label: string;
    team1Id: string | null;
    team2Id: string | null;
    winnerId: string | null;
    scheduledDate: string;
    scheduledTime: string;
    matchDay: number;
    bestOf?: number;
    mvpPlayerName?: string;
    games?: {
        gameNumber: number;
        winnerId: string | null;
        mvpPlayerName?: string;
    }[];
}

export interface MobaBracketData {
    teams: MobaTeam[];
    groups: { label: string; teamIds: string[] }[];
    groupMatches: MobaGroupMatch[];
    knockoutMatches: MobaKnockoutMatch[];
    mvpTally?: Record<string, { playerName: string; teamId?: string; teamName?: string; squadId?: string; squadName?: string; count: number }>;
    tournamentMvpId?: string;
}

// ─── Squad Types ────────────────────────────────────────────────────────────

export interface SquadMember {
    uid: string;
    username: string;
    role: 'leader' | 'co-leader' | 'member' | 'substitute';
    status?: string;
    avatar?: string;
    photoURL?: string;
    ign?: string;
    gameId?: string;
    slotLabel?: string;
}

export interface Squad {
    id: string;
    name: string;
    game: string;
    tag?: string;
    status: string;
    leaderUid?: string;
    memberUids?: string[];
    members: SquadMember[];
    color?: string;
    region?: string;
    rank?: string;
    nameLastChanged?: unknown;
}
